import { ExercisePrerequisiteDTO } from '@/generated';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, TextField } from '@mui/material';
import { useEffect, useState } from 'react';

type ExercisePrerequisiteModalProps = {
  show: boolean;
  prerequisite?: ExercisePrerequisiteDTO;
  onHide: () => void;
  onSave: (prerequisite: ExercisePrerequisiteDTO) => void;
};

export const ExercisePrerequisiteModal = (props: ExercisePrerequisiteModalProps) => {
  const [draft, setDraft] = useState<ExercisePrerequisiteDTO>();
  const [error, setError] = useState('');

  useEffect(() => {
    if (props.show) {
      setDraft(props.prerequisite ? { ...props.prerequisite } : undefined);
      setError('');
    }
  }, [props.prerequisite, props.show]);

  const setDraftField = (field: Exclude<keyof ExercisePrerequisiteDTO, 'exerciseId'>, value: string) => {
    setDraft((current) => (current ? { ...current, [field]: value === '' ? undefined : Number(value) } : current));
    setError('');
  };

  const save = () => {
    if (!draft) return;
    const requirements = [draft.minSets, draft.minReps, draft.minDurationSeconds, draft.minLoadKg];
    if (requirements.some((value) => value !== undefined && (!Number.isFinite(value) || value < 0))) {
      setError('Requirements must be positive numbers.');
      return;
    }
    if (!requirements.some((value) => value !== undefined && value > 0)) {
      setError('Enter at least one requirement greater than zero.');
      return;
    }
    props.onSave(draft);
  };

  return (
    <Dialog open={props.show} onClose={props.onHide} fullWidth maxWidth="sm">
      <DialogTitle>Prerequisite requirements</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Minimum sets" type="number" value={draft?.minSets ?? ''} onChange={(event) => setDraftField('minSets', event.target.value)} />
          <TextField label="Minimum reps" type="number" value={draft?.minReps ?? ''} onChange={(event) => setDraftField('minReps', event.target.value)} />
          <TextField
            label="Minimum duration (seconds)"
            type="number"
            value={draft?.minDurationSeconds ?? ''}
            onChange={(event) => setDraftField('minDurationSeconds', event.target.value)}
          />
          <TextField label="Minimum load (kg)" type="number" value={draft?.minLoadKg ?? ''} onChange={(event) => setDraftField('minLoadKg', event.target.value)} />
          {error && <DialogContentText color="error">{error}</DialogContentText>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onHide}>Cancel</Button>
        <Button onClick={save}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};
